import { toast } from 'sonner';
/*
 * hyperion-index:begin
 * ---
 * path: hyperion-fe/src/platform/observability/debug-logger.ts
 * layer: web
 * area: lib
 * kind: lib
 * web_root: lib
 * relpath: web/src/lib/debug-logger.ts
 * filename: debug-logger.ts
 * extension: ts
 * ---
 * hyperion-index:end
 */

/**
 * Debug Logger — captures EVERYTHING for troubleshooting.
 *
 * Logs every API call (request + response + error), console output,
 * navigation, React errors, and user interactions to a rolling buffer.
 *
 * Keyboard shortcut: Ctrl+Shift+L — downloads the log as a file.
 * Auto-dumps on unhandled errors.
 *
 * Enable by setting localStorage.setItem('hyperion_debug', 'true')
 * or by appending ?debug=true to any URL.
 */

const MAX_ENTRIES = 5000;
const LOG_KEY = 'hyperion_debug_log';
const ENABLED_KEY = 'hyperion_debug';
const SENSITIVE_KEY = /authorization|cookie|credential|password|secret|token|api[_-]?key/i;
const SENSITIVE_TEXT =
  /((?:authorization|cookie|credential|password|secret|token|api[_-]?key)\s*[:=]\s*)[^\s,;]+/gi;
const SENSITIVE_API_PATH =
  /\/(?:vault\/credentials\/[^/?]+\/reveal(?:-all)?|vault\/totp\/[^/?]+\/code|cerberus\/reveal\/[^/?]+|ai\/chatgpt|dispatch\/preferences)(?:[/?]|$)/i;

interface LogEntry {
  t: string; // ISO timestamp
  type:
    | 'api-req'
    | 'api-res'
    | 'api-err'
    | 'error'
    | 'warn'
    | 'info'
    | 'nav'
    | 'click'
    | 'react-error'
    | 'console';
  msg: string;
  data?: unknown;
}

let buffer: LogEntry[] = [];
let initialized = false;

function isEnabled(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('debug') === 'true') {
      localStorage.setItem(ENABLED_KEY, 'true');
      return true;
    }
    return localStorage.getItem(ENABLED_KEY) === 'true';
  } catch {
    return false;
  }
}

function push(entry: LogEntry) {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) {
    buffer = buffer.slice(-MAX_ENTRIES);
  }
  // Also persist to sessionStorage so it survives soft navigations
  try {
    sessionStorage.setItem(LOG_KEY, JSON.stringify(buffer.slice(-500)));
  } catch {
    // Storage full — ignore
  }
}

function ts(): string {
  return new Date().toISOString();
}

export function redactDebugValue(value: unknown, depth = 0): unknown {
  if (depth > 8) return '[TRUNCATED]';
  if (typeof value === 'string') return value.replace(SENSITIVE_TEXT, '$1[REDACTED]');
  if (Array.isArray(value)) return value.map((item) => redactDebugValue(item, depth + 1));
  if (value && typeof value === 'object') {
    const redacted: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      redacted[key] = SENSITIVE_KEY.test(key) ? '[REDACTED]' : redactDebugValue(item, depth + 1);
    }
    return redacted;
  }
  return value;
}

export function redactApiDebugValue(url: string, value: unknown): unknown {
  return SENSITIVE_API_PATH.test(url) ? '[REDACTED]' : value;
}

function safe(obj: unknown, maxLen = 2000): unknown {
  try {
    const redacted = redactDebugValue(obj);
    const str = JSON.stringify(redacted);
    if (str && str.length > maxLen) {
      return str.substring(0, maxLen) + '... [truncated]';
    }
    return redacted;
  } catch {
    return String(obj).replace(SENSITIVE_TEXT, '$1[REDACTED]');
  }
}

// ── Public API ──

export function debugLog(type: LogEntry['type'], msg: string, data?: unknown) {
  if (!isEnabled()) return;
  push({ t: ts(), type, msg, data: data ? safe(data) : undefined });
}

export function debugApiRequest(method: string, url: string, body?: unknown) {
  if (!isEnabled()) return;
  push({
    t: ts(),
    type: 'api-req',
    msg: `${method.toUpperCase()} ${url}`,
    data: body ? safe(redactApiDebugValue(url, body), 500) : undefined,
  });
}

export function debugApiResponse(
  method: string,
  url: string,
  status: number,
  data?: unknown,
  durationMs?: number
) {
  if (!isEnabled()) return;
  push({
    t: ts(),
    type: 'api-res',
    msg: `${status} ${method.toUpperCase()} ${url} (${durationMs ?? '?'}ms)`,
    data: safe(redactApiDebugValue(url, data), 1000),
  });
}

export function debugApiError(
  method: string,
  url: string,
  status: number | string,
  error: unknown
) {
  if (!isEnabled()) return;
  push({
    t: ts(),
    type: 'api-err',
    msg: `ERROR ${status} ${method.toUpperCase()} ${url}`,
    data: safe(redactApiDebugValue(url, error)),
  });
}

export function debugReactError(componentName: string, error: unknown) {
  if (!isEnabled()) return;
  push({
    t: ts(),
    type: 'react-error',
    msg: `React error in ${componentName}`,
    data: error instanceof Error ? { message: error.message, stack: error.stack } : safe(error),
  });
  // Auto-dump on React errors
  autoDump('react-error');
}

export function dumpLog(): string {
  const header = [
    '=== HYPERION DEBUG LOG ===',
    `Dumped: ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `User Agent: ${navigator.userAgent}`,
    `Entries: ${buffer.length}`,
    `Session Storage entries: ${(() => {
      try {
        const s = sessionStorage.getItem(LOG_KEY);
        return s ? JSON.parse(s).length : 0;
      } catch {
        return 0;
      }
    })()}`,
    '===========================',
    '',
  ].join('\n');

  const lines = buffer.map((e) => {
    const dataStr =
      e.data !== undefined
        ? ` | ${typeof e.data === 'string' ? e.data : JSON.stringify(e.data)}`
        : '';
    return `[${e.t}] [${e.type.toUpperCase().padEnd(11)}] ${e.msg}${dataStr}`;
  });

  return header + lines.join('\n');
}

export function downloadLog() {
  const content = dumpLog();
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const now = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  a.href = url;
  a.download = `hyperion-debug-${now}.log`;
  a.click();
  URL.revokeObjectURL(url);
}

function autoDump(reason: string) {
  debugLog('info', `Auto-dump triggered: ${reason}`);
  try {
    const content = dumpLog();
    // Store in localStorage so it persists across page reloads
    localStorage.setItem('hyperion_last_crash_log', content);
    localStorage.setItem('hyperion_last_crash_time', new Date().toISOString());
  } catch {
    // Storage full
  }
}

// ── Initialization ──

export function initDebugLogger() {
  if (initialized) return;
  initialized = true;

  if (!isEnabled()) {
    // Still register the keyboard shortcut so debug can be enabled anytime
    document.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'L') {
        if (!isEnabled()) {
          localStorage.setItem(ENABLED_KEY, 'true');
          toast.info('Hyperion debug logging enabled', {
            description: 'Reload the page to start capturing. Press Ctrl+Shift+L again to download logs.',
          });
        } else {
          downloadLog();
        }
      }
    });
    return;
  }

  debugLog('info', 'Debug logger initialized');
  debugLog('info', `URL: ${window.location.href}`);

  // ── Capture console.error and console.warn ──
  const origError = console.error;
  const origWarn = console.warn;

  console.error = (...args: unknown[]) => {
    push({
      t: ts(),
      type: 'error',
      msg: args
        .map((a) => (typeof a === 'string' ? a : JSON.stringify(a)))
        .join(' ')
        .substring(0, 2000),
    });
    origError.apply(console, args);
  };

  console.warn = (...args: unknown[]) => {
    push({
      t: ts(),
      type: 'warn',
      msg: args
        .map((a) => (typeof a === 'string' ? a : JSON.stringify(a)))
        .join(' ')
        .substring(0, 2000),
    });
    origWarn.apply(console, args);
  };

  // ── Capture unhandled errors ──
  window.addEventListener('error', (event) => {
    push({
      t: ts(),
      type: 'error',
      msg: `Unhandled: ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`,
    });
    autoDump('unhandled-error');
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason.message : String(event.reason);
    push({
      t: ts(),
      type: 'error',
      msg: `Unhandled promise rejection: ${reason}`,
      data: event.reason instanceof Error ? event.reason.stack : undefined,
    });
    autoDump('unhandled-rejection');
  });

  // ── Capture navigation ──
  const origPushState = history.pushState;
  const origReplaceState = history.replaceState;

  history.pushState = function (...args) {
    push({ t: ts(), type: 'nav', msg: `Navigate: ${args[2] ?? window.location.pathname}` });
    return origPushState.apply(this, args);
  };

  history.replaceState = function (...args) {
    push({ t: ts(), type: 'nav', msg: `Replace: ${args[2] ?? window.location.pathname}` });
    return origReplaceState.apply(this, args);
  };

  window.addEventListener('popstate', () => {
    push({ t: ts(), type: 'nav', msg: `Back/Forward: ${window.location.pathname}` });
  });

  // ── Capture clicks on interactive elements ──
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // Only log clicks on buttons, links, and inputs
      const tag = target.tagName.toLowerCase();
      if (
        !['button', 'a', 'input', 'select', 'textarea'].includes(tag) &&
        !target.closest('button') &&
        !target.closest('a') &&
        !target.getAttribute('role')
      ) {
        return;
      }

      const btn = target.closest('button') || target.closest('a') || target;
      const text = (btn.textContent || '').trim().substring(0, 100);
      const id = btn.id ? `#${btn.id}` : '';
      const cls = btn.className ? `.${String(btn.className).split(' ')[0]}` : '';
      const href = (btn as HTMLAnchorElement).href ? ` → ${(btn as HTMLAnchorElement).href}` : '';

      push({
        t: ts(),
        type: 'click',
        msg: `Click: <${tag}${id}${cls}> "${text}"${href}`,
      });
    },
    { capture: true }
  );

  // ── Keyboard shortcut: Ctrl+Shift+L to download ──
  document.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'L') {
      e.preventDefault();
      downloadLog();
    }
  });

  // ── Load previous session's buffer if it exists ──
  try {
    const prev = sessionStorage.getItem(LOG_KEY);
    if (prev) {
      const prevEntries = JSON.parse(prev) as LogEntry[];
      buffer = [...prevEntries, ...buffer];
    }
  } catch {
    // Ignore
  }

  // ── Show indicator that debug mode is active ──
  const indicator = document.createElement('div');
  indicator.id = 'hyperion-debug-indicator';
  indicator.textContent = 'DEBUG';
  indicator.title = 'Debug logging active. Ctrl+Shift+L to download log.';
  indicator.style.cssText =
    'position:fixed;bottom:4px;right:4px;background:#ef4444;color:white;font-size:9px;font-weight:bold;padding:2px 6px;border-radius:4px;z-index:99999;opacity:0.7;pointer-events:none;font-family:monospace;';
  document.body?.appendChild(indicator);
}
